import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import authService, {
  User,
  LoginCredentials,
  RegisterData,
  AuthMembership,
  TenantAuthResponse,
  MembershipRole,
  onAuthSync,
} from '@services/auth.service';
import usersService from '@services/users.service';
import { getApiErrorMessage } from '@utils/apiError';
import { hasActiveTenant } from '@utils/tenant';
import { clearTenantScopedSessionState } from '@utils/tenantSession';
import { getRoleLandingPath } from '../config/roleWorkspace';

interface AuthContextType {
  user: User | null;
  activeCompanyId: string | null;
  membershipRole: MembershipRole | null;
  memberships: AuthMembership[];
  hasActiveTenant: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  syncSession: () => Promise<void>;
  switchTenant: (companyId: string, options?: { navigateTo?: string | false }) => Promise<void>;
  completeCompanyLink: (companyId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const [membershipRole, setMembershipRole] = useState<MembershipRole | null>(null);
  const [memberships, setMemberships] = useState<AuthMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const applyAuthResponse = useCallback((response: TenantAuthResponse) => {
    const role =
      response.membershipRole ??
      response.memberships.find((m) => m.companyId === response.activeCompanyId)?.role ??
      null;

    setUser({
      ...response.user,
      activeCompanyId: response.activeCompanyId || null,
      membershipRole: role ?? undefined,
    });
    setActiveCompanyId(response.activeCompanyId || null);
    setMembershipRole(role);
    setMemberships(response.memberships ?? []);
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setActiveCompanyId(null);
    setMembershipRole(null);
    setMemberships([]);
  }, []);

  const applyProfile = useCallback((profile: User & { memberships?: AuthMembership[] }) => {
    setMemberships((prevMemberships) => {
      const nextMemberships = profile.memberships ?? prevMemberships;

      setActiveCompanyId((prevActiveCompanyId) => {
        const nextActiveCompanyId = profile.activeCompanyId ?? prevActiveCompanyId;
        const nextRole =
          profile.membershipRole ??
          nextMemberships.find((m) => m.companyId === nextActiveCompanyId)?.role ??
          null;

        setMembershipRole(nextRole);
        setUser({
          ...profile,
          activeCompanyId: nextActiveCompanyId,
          membershipRole: nextRole ?? undefined,
        });

        return nextActiveCompanyId;
      });

      return profile.memberships ?? prevMemberships;
    });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      if (!authService.isAuthenticated()) {
        clearAuth();
        return;
      }

      const profile = await authService.getProfile();
      applyProfile(profile);

      if (!profile.memberships?.length && authService.getRefreshToken()) {
        try {
          const session = await authService.refreshSession();
          applyAuthResponse(session);
        } catch {
          // keep profile from /auth/me
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [applyAuthResponse, applyProfile, clearAuth]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    return onAuthSync((response) => {
      applyAuthResponse(response);
    });
  }, [applyAuthResponse]);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    try {
      setIsLoading(true);
      const response = await authService.login(credentials);
      applyAuthResponse(response);
      return {
        ...response.user,
        activeCompanyId: response.activeCompanyId || null,
        membershipRole:
          response.membershipRole ??
          response.memberships.find((m) => m.companyId === response.activeCompanyId)?.role,
      };
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(getApiErrorMessage(error, 'Login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authService.register(data);
      applyAuthResponse(response);
      return hasActiveTenant(response.activeCompanyId);
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await authService.logout();
      clearAuth();
    } catch (error) {
      console.error('Logout failed:', error);
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!authService.isAuthenticated()) return;
    const profile = await authService.getProfile();
    applyProfile(profile);
  };

  const syncSession = async () => {
    const response = await authService.refreshSession();
    applyAuthResponse(response);
  };

  const switchTenant = async (
    companyId: string,
    options?: { navigateTo?: string | false },
  ) => {
    const response = await authService.switchTenant(companyId);
    clearTenantScopedSessionState();
    applyAuthResponse(response);

    if (options?.navigateTo === false) {
      return;
    }

    if (typeof options?.navigateTo === 'string') {
      window.location.assign(options.navigateTo);
      return;
    }

    const role =
      response.membershipRole ??
      response.memberships.find((m) => m.companyId === response.activeCompanyId)?.role ??
      null;
    window.location.assign(getRoleLandingPath(role));
  };

  const completeCompanyLink = async (companyId: string) => {
    await usersService.linkCompany(companyId);
    await syncSession();
  };

  const value: AuthContextType = {
    user,
    activeCompanyId,
    membershipRole,
    memberships,
    hasActiveTenant: hasActiveTenant(activeCompanyId),
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    syncSession,
    switchTenant,
    completeCompanyLink,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
